import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { User, UserRole } from '@/types'
import { useLocation } from 'react-router-dom'
import ProviderNotVerifiedPage from './ProviderNotVerifiedPage'
import ProviderBlockedPage from './ProviderBlockedPage'
import api from '@/services/api'
import SubscriptionRequiredPopup from './SubscriptionRequiredPopup'

interface AgencyVerificationGuardProps {
  children: React.ReactNode
}

enum AgencyStatus {
  ACTIVE = 'active',
  BLOCKED = 'blocked',
  PENDING = 'pending',
}

const AgencyVerificationGuard: React.FC<AgencyVerificationGuardProps> = ({
  children,
}) => {
  const { user, role } = useAuth()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(true)
  const [agencyStatus, setAgencyStatus] = useState<string>('pending')
  const [blockReason, setBlockReason] = useState<string | null>(null)
  const [latestUserData, setLatestUserData] = useState<User | null>(null);
  useEffect(() => {
    const fetchAgencyProfile = async () => {
      if (role !== UserRole.AGENCY || !user) {
        setIsChecking(false)
        return
      }

      try {
        const response = await api.get('/agency/profile')
        const profileData = response?.data?.data || response?.data
        setLatestUserData(profileData)
        if (profileData) {
          setAgencyStatus(profileData.status || 'pending')
          setBlockReason(profileData.blockReason || null)
        }
      } catch (error) {
        console.error('Error fetching agency profile:', error)
      } finally {
        setIsChecking(false)
      }
    }

    fetchAgencyProfile()
  }, [user, role, location.pathname])

  if (isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Verifying agency status...
        </Typography>
      </Box>
    )
  }

  if (role !== UserRole.AGENCY) {
    return <>{children}</>
  }

  const isSettingsOrDocuments =
    location.pathname.startsWith('/account-settings') ||
    location.pathname.startsWith('/agency-documents') ||
    location.pathname.startsWith('/dashboard')

  if (isSettingsOrDocuments) {
    return <>{children}</>
  }

  const agencyName = user ? `${user.firstName} ${user.lastName}` : undefined

  if (agencyStatus === AgencyStatus.BLOCKED) {
    return (
      <ProviderBlockedPage
        providerName={agencyName}
        blockReason={blockReason || 'Your agency account has been blocked.'}
      />
    )
  }

 
  if (latestUserData && !latestUserData.isSubscriptionTaken) {
    return <SubscriptionRequiredPopup providerName={agencyName} />
  }
   if (agencyStatus === AgencyStatus.PENDING) {
    return <ProviderNotVerifiedPage providerName={agencyName} />
  }

  return <>{children}</>
}

export default AgencyVerificationGuard
